import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { Period } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import type { RESTRICTION_MODEL_CONTEXTS } from 'lumen/utils/constants/channel-pricing-constants';
import { RESTRICTION_TYPES } from 'lumen/utils/constants/channel-pricing-constants';
import type { AbstractActionValue } from 'lumen/utils/constants/restriction-actions';
import { ABSTRACT_ACTIONS } from 'lumen/utils/constants/restriction-actions';
import {
  isDeepDiscountRateGroup,
  isHotelLevel,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';

type RestrictionValue = {
  type: (typeof RESTRICTION_TYPES)[keyof typeof RESTRICTION_TYPES];
  value: number;
};

export default class RestrictionAction extends Model {
  [Type] = 'restriction-action' as const;

  /* Belongs to period rather than restriction: an action can exist when no restriction is currently in place (e.g. first-ever push for this context), so there is no restriction record to link to. See: https://lighthouseintelligence.atlassian.net/wiki/x/WQB_hAE */
  @belongsTo('period', { async: false, inverse: 'restrictionActions' })
  declare period: Period;

  @attr('string') declare action: AbstractActionValue;
  @attr('number') declare actionedUserId: number;
  // Note: autopilot hasn't been relevant for restrictions right now. Made this a string for now. To be checked further later
  @attr('string') declare autopilotScheduleTimestamp: string | null;
  @attr('string') declare bulkPushId: string | null;
  @attr('string') declare channelId: string | null;
  @attr('string')
  declare context: (typeof RESTRICTION_MODEL_CONTEXTS)[keyof typeof RESTRICTION_MODEL_CONTEXTS];
  @attr('dayjs') declare createdAt: DayjsTransformReturn;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare otaId: string | null;
  @attr('string') declare pmsStateText: string;
  @attr('string') declare rateGroupId: string | null;
  @attr('string') declare rateId: string | null;
  @attr('string') declare roomId: string | null;
  @attr('string') declare source: string;
  /** Latest attempted restriction state, including failed pushes. Always populated. */
  @attr('json') declare values: RestrictionValue[];

  get hasException(): boolean {
    return this.action === ABSTRACT_ACTIONS.EXCEPTION;
  }

  // Whether this action carries a MinLOS value, regardless of its action state.
  get hasMinLos(): boolean {
    return this.minLosAttemptedValue != null;
  }

  // A `hasException` action may carry attempted values for several restriction
  // types. Guard on `hasMinLos` so we only flag actions whose failed push
  // actually targeted MinLOS.
  get hasMinLosException(): boolean {
    return this.hasException && this.hasMinLos;
  }

  // A `hasException` action may carry attempted values for non-overbooking
  // restriction types (e.g. min-LOS). Guard on `overbookingAttemptedValue`
  // so we only flag actions whose failed push actually targeted overbooking.
  get hasOverbookingException(): boolean {
    return this.hasException && this.overbookingAttemptedValue != null;
  }

  get isAccepted(): boolean {
    return this.action === ABSTRACT_ACTIONS.ACCEPTED;
  }

  get isBeingPushed(): boolean {
    return this.isAccepted || this.isPushing;
  }

  get isDeepDiscountRateGroup(): boolean {
    return isDeepDiscountRateGroup(this);
  }

  get isHotelLevel(): boolean {
    return isHotelLevel(this);
  }

  get isPushing(): boolean {
    return this.action === ABSTRACT_ACTIONS.PUSHING;
  }

  get minLosAttemptedValue(): number | null {
    return (
      this.values.find((value) => value.type === RESTRICTION_TYPES.MIN_LOS)
        ?.value ?? null
    );
  }

  get overbookingAttemptedValue(): number | null {
    return (
      this.values.find((value) => value.type === RESTRICTION_TYPES.OVERBOOKING)
        ?.value ?? null
    );
  }
}
